
import React, { useMemo } from 'react';
import { Property, PropertyStatus, SubscriptionPlan } from '../types';
import { TRANSLATIONS } from '../constants';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area 
} from 'recharts';
import { motion } from 'framer-motion';

// CRA/TS in this workspace sometimes infers `motion.div` as `unknown`.
// This cast avoids TS build failure.
const MotionDiv = motion.div as any;

import { 
  TrendingUp, TrendingDown, DollarSign, Percent, Activity, Download, 
  AlertTriangle, ArrowUpRight, ArrowDownRight, Home, Building2, AlertCircle, Wallet, CheckCircle2,
  Lock, Crown
} from 'lucide-react';

interface AnalyticsProps {
  properties: Property[];
  lang: 'en' | 'pt';
  subscriptionPlan?: SubscriptionPlan;
  onUpgradeRequired?: () => void;
}

const Analytics: React.FC<AnalyticsProps> = ({ properties, lang, subscriptionPlan = 'Starter', onUpgradeRequired }) => {
  const t = (key: string) => TRANSLATIONS[key] ? TRANSLATIONS[key][lang] : key;

  const isLocked = subscriptionPlan === 'Starter';

  // --- 1. CALCULATIONS & METRICS ---
  const metrics = useMemo(() => {
    const totalValue = properties.reduce((acc, p) => acc + p.currentValue, 0);
    const totalRevenueMonthly = properties.reduce((acc, p) => acc + p.monthlyRent, 0);
    const totalExpensesMonthly = properties.reduce((acc, p) => acc + p.expenses, 0);
    const totalRevenueAnnual = totalRevenueMonthly * 12;
    const totalNOIAnnual = (totalRevenueMonthly - totalExpensesMonthly) * 12;
    
    const totalPurchasePrice = properties.reduce((acc, p) => acc + p.purchasePrice, 0);
    const totalLoan = properties.reduce((acc, p) => acc + (p.loanAmount || 0), 0);
    const totalInvestment = Math.max(totalPurchasePrice - totalLoan, 1); // Avoid div by 0 (Equity)

    const avgCapRate = totalPurchasePrice > 0 ? (totalNOIAnnual / totalPurchasePrice) * 100 : 0;
    const avgCashOnCash = totalInvestment > 0 ? ((totalNOIAnnual - (totalLoan * 0.04)) / totalInvestment) * 100 : 0; // Simplified debt service (4% interest assumption for aggregate)

    const occupiedCount = properties.filter(p => p.status === PropertyStatus.Occupied).length;
    const occupancyRate = properties.length > 0 ? (occupiedCount / properties.length) * 100 : 0;
    const accumulatedProfit = totalNOIAnnual; // Simplified accumulated profit (1 year projection)

    return {
      totalValue,
      totalRevenueAnnual,
      accumulatedProfit,
      avgCapRate,
      avgCashOnCash,
      occupancyRate,
      totalRevenueMonthly,
      totalNOIAnnual
    };
  }, [properties]);

  // --- 2. DATA PREPARATION FOR VISUALS ---
  

  const lockedScreen = (
    <div className="h-full flex items-center justify-center p-8" data-testid="analytics-locked">
      <div className="max-w-lg text-center space-y-6">
        <div className="w-24 h-24 bg-slate-900 rounded-full flex items-center justify-center mx-auto border border-slate-800 shadow-2xl relative">
          <Activity className="w-10 h-10 text-slate-500" />
          <div className="absolute -top-1 -right-1 bg-amber-500 p-1.5 rounded-full border-4 border-slate-950">
            <Lock className="w-4 h-4 text-black" />
          </div>
        </div>
        <div>
          <h2 className="text-3xl font-black text-white mb-2" data-testid="analytics-locked-title">Analytics Cockpit Locked</h2>
          <p className="text-slate-400" data-testid="analytics-locked-description">
            Upgrade to the <strong>Investor</strong> plan to unlock comprehensive portfolio performance tracking and visualization.
          </p>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 text-left space-y-3">
          <div className="flex items-center gap-3 text-sm text-slate-300">
            <div className="p-1 bg-emerald-500/10 rounded"><Crown className="w-3 h-3 text-emerald-500" /></div>
            Visual trend analysis (Revenue vs Expenses)
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-300">
            <div className="p-1 bg-emerald-500/10 rounded"><Crown className="w-3 h-3 text-emerald-500" /></div>
            Occupancy tracking & Geographic distribution
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-300">
            <div className="p-1 bg-emerald-500/10 rounded"><Crown className="w-3 h-3 text-emerald-500" /></div>
            Top & Bottom performer ranking
          </div>
        </div>
        <button
          onClick={onUpgradeRequired}
          className="px-8 py-3 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white rounded-xl font-bold shadow-lg shadow-emerald-900/20 transition-all transform hover:-translate-y-1"
          data-testid="analytics-unlock-button"
        >
          Unlock Analytics
        </button>
      </div>
    </div>
  );

  // Rank Properties by Monthly Cashflow
  const rankedProperties = useMemo(() => {
    return [...properties].sort((a, b) => {
      const cfA = a.monthlyRent - a.expenses;
      const cfB = b.monthlyRent - b.expenses;
      return cfB - cfA; // Descending
    });
  }, [properties]);

  const topPerformers = rankedProperties.slice(0, 3);
  const bottomPerformers = [...rankedProperties].reverse().slice(0, 3);

  // Mock Data Generation for Charts (Simulating 12 months history)
  const generateMockTrend = (baseValue: number, variance: number) => {
    return Array.from({ length: 12 }, (_, i) => ({
      month: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][i],
      value: baseValue + (Math.random() * variance * 2 - variance)
    }));
  };

  const portfolioTrendData = useMemo(() => {
      return Array.from({ length: 12 }, (_, i) => {
          const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
          // Simulate some growth
          const growthFactor = 1 + (i * 0.005); 
          return {
              month: monthNames[i],
              revenue: metrics.totalRevenueMonthly * growthFactor * (0.95 + Math.random() * 0.1),
              expenses: metrics.totalRevenueMonthly * 0.2 * growthFactor * (0.9 + Math.random() * 0.2)
          };
      });
  }, [metrics.totalRevenueMonthly]);

  const incomeByCity = useMemo(() => {
      const distribution: Record<string, number> = {};
      properties.forEach(p => {
          distribution[p.city] = (distribution[p.city] || 0) + p.monthlyRent;
      });
      return Object.entries(distribution).map(([name, value]) => ({ name, value }));
  }, [properties]);

  // Smart Alerts Logic
  const alerts = useMemo(() => {
      const list = [];
      const avgExp = metrics.totalRevenueMonthly * 0.2 / (properties.length || 1); // Assume 20% expense ratio is normal avg

      properties.forEach(p => {
          const capRate = p.purchasePrice > 0 ? ((p.monthlyRent - p.expenses) * 12 / p.purchasePrice) * 100 : 0;
          
          if (p.status === PropertyStatus.Vacant) {
              list.push({ type: 'critical', msg: `Vacancy: ${p.address}`, icon: AlertCircle });
          }
          if (p.expenses > avgExp * 1.5) {
              list.push({ type: 'warning', msg: `High Expenses: ${p.address}`, icon: TrendingDown });
          }
          if (capRate < 3 && p.status === PropertyStatus.Occupied) {
              list.push({ type: 'warning', msg: `Low Cap Rate (<3%): ${p.address}`, icon: Percent });
          }
      });
      return list;
  }, [properties, metrics]);

  const CHART_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  const handleExport = () => {
      window.print();
  };

  return (
    isLocked ? lockedScreen : (
    <div className="p-8 max-w-[1600px] mx-auto space-y-8 pb-24 h-full overflow-y-auto custom-scrollbar">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-end gap-4">
        <div>
           <h2 className="text-4xl font-black text-white tracking-tight mb-1 flex items-center gap-3">
             <Activity className="w-8 h-8 text-emerald-500" />
             Analytics Cockpit
           </h2>
           <p className="text-slate-400 font-mono text-xs tracking-widest">PORTFOLIO PERFORMANCE INTELLIGENCE</p>
        </div>
        <button 
            onClick={handleExport}
            className="bg-slate-800 hover:bg-slate-700 text-white px-5 py-2.5 rounded-xl border border-slate-700 flex items-center gap-2 transition-all font-bold"
        >
            <Download className="w-4 h-4" /> Export Report
        </button>
      </div>

      {/* 1. KPI PANEL */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <KPICard label="Market Value" value={`€${(metrics.totalValue / 1000).toFixed(1)}k`} sub="Total Assets" icon={Building2} color="blue" />
          <KPICard label="Annual Revenue" value={`€${(metrics.totalRevenueAnnual / 1000).toFixed(1)}k`} sub="Gross Income" icon={Wallet} color="emerald" />
          <KPICard label="Net Profit (YTD)" value={`€${(metrics.accumulatedProfit / 1000).toFixed(1)}k`} sub="Est. Annual NOI" icon={TrendingUp} color="emerald" />
          <KPICard label="Avg Cap Rate" value={`${metrics.avgCapRate.toFixed(1)}%`} sub="Portfolio Avg" icon={Percent} color="indigo" />
          <KPICard label="Cash-on-Cash" value={`${metrics.avgCashOnCash.toFixed(1)}%`} sub="Return on Equity" icon={DollarSign} color="amber" />
          <KPICard label="Occupancy" value={`${metrics.occupancyRate.toFixed(0)}%`} sub={`${properties.length} Units`} icon={Home} color={metrics.occupancyRate > 90 ? 'emerald' : 'red'} />
      </div>

      {/* 2. MAIN CONTENT GRID */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          
          {/* LEFT COL: CHARTS */}
          <div className="xl:col-span-2 space-y-8">

              {/* Trend Chart */}
              <MotionDiv 
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                className="bg-slate-900/60 backdrop-blur-md border border-slate-800 rounded-3xl p-6 h-[400px]"
              >
                  <div className="flex justify-between items-center mb-6">
                      <h3 className="text-lg font-bold text-white flex items-center gap-2">
                          <TrendingUp className="w-5 h-5 text-blue-500" /> Financial Performance (12 Months)
                      </h3>
                      <div className="flex gap-4 text-xs">
                          <div className="flex items-center gap-1 text-slate-400"><div className="w-2 h-2 bg-emerald-500 rounded-full"></div> Revenue</div>
                          <div className="flex items-center gap-1 text-slate-400"><div className="w-2 h-2 bg-blue-500 rounded-full"></div> Net Profit</div>
                      </div>
                  </div>
                  <ResponsiveContainer width="100%" height="85%">
                      <AreaChart data={portfolioTrendData}>
                          <defs>
                              <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                              </linearGradient>
                              <linearGradient id="colorExp" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                              </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                          <XAxis dataKey="month" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                          <YAxis stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(val) => `€${val}`} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#0f172a', borderColor: '#1e293b', borderRadius: '12px', color: '#fff' }}
                            itemStyle={{ color: '#fff' }}
                          />
                          <Area type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorRev)" />
                          <Area type="monotone" dataKey="expenses" stroke="#ef4444" strokeWidth={3} fillOpacity={1} fill="url(#colorExp)" />
                      </AreaChart>
                  </ResponsiveContainer>
              </MotionDiv>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Income Distribution */}
                  <MotionDiv 
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                    className="bg-slate-900/60 backdrop-blur-md border border-slate-800 rounded-3xl p-6 h-[350px]"
                  >
                      <h3 className="text-lg font-bold text-white mb-4">Revenue by Zone</h3>
                      <ResponsiveContainer width="100%" height="85%">
                          <PieChart>
                              <Pie
                                  data={incomeByCity}
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={60}
                                  outerRadius={80}
                                  paddingAngle={5}
                                  dataKey="value"
                              >
                                  {incomeByCity.map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                  ))}
                              </Pie>
                              <Tooltip contentStyle={{ backgroundColor: '#0f172a', borderColor: '#1e293b', borderRadius: '8px', color: '#fff' }} />
                              <Legend />
                          </PieChart>
                      </ResponsiveContainer>
                  </MotionDiv>

                  {/* Occupancy / Vacancy Bar */}
                  <MotionDiv 
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                    className="bg-slate-900/60 backdrop-blur-md border border-slate-800 rounded-3xl p-6 h-[350px]"
                  >
                      <h3 className="text-lg font-bold text-white mb-4">Occupancy Status</h3>
                      <ResponsiveContainer width="100%" height="85%">
                          <BarChart data={[
                              { name: 'Occupied', value: metrics.occupancyRate },
                              { name: 'Vacant', value: 100 - metrics.occupancyRate },
                          ]}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                              <XAxis dataKey="name" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                              <YAxis stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                              <Tooltip cursor={{fill: '#1e293b'}} contentStyle={{ backgroundColor: '#0f172a', borderColor: '#1e293b', borderRadius: '8px', color: '#fff' }} />
                              <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                                  {
                                    [0,1].map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={index === 0 ? '#10b981' : '#ef4444'} />
                                    ))
                                  }
                              </Bar>
                          </BarChart>
                      </ResponsiveContainer>
                  </MotionDiv>
              </div>
          </div>

          {/* RIGHT COL: PERFORMERS & ALERTS */}
          <div className="space-y-8">
              {/* Top Performers */}
              <div className="bg-slate-900/60 backdrop-blur-md border border-slate-800 rounded-3xl p-6">
                  <h3 className="text-sm font-bold text-slate-400 uppercase mb-4 flex items-center gap-2">
                      <ArrowUpRight className="w-4 h-4 text-emerald-500" /> Top 3 Performers (Cashflow)
                  </h3>
                  <div className="space-y-3">
                      {topPerformers.map((p, idx) => (
                          <div key={p.id} className="flex items-center justify-between p-3 bg-slate-950/50 rounded-xl border border-slate-800 hover:border-emerald-500/30 transition-colors">
                              <div className="flex items-center gap-3">
                                  <div className="w-6 h-6 rounded-full bg-emerald-500/10 text-emerald-500 flex items-center justify-center text-xs font-bold">
                                      {idx + 1}
                                  </div>
                                  <div>
                                      <p className="text-sm font-bold text-white truncate w-28">{p.address.split(',')[0]}</p>
                                      <p className="text-[10px] text-slate-500">{p.city}</p>
                                  </div>
                              </div>
                              <div className="text-right">
                                  <p className="text-emerald-400 font-mono font-bold text-sm">+€{p.monthlyRent - p.expenses}</p>
                                  <p className="text-[10px] text-slate-500">Net / Mo</p>
                              </div>
                          </div>
                      ))}
                  </div>
              </div>

              {/* Bottom Performers */}
              <div className="bg-slate-900/60 backdrop-blur-md border border-slate-800 rounded-3xl p-6">
                  <h3 className="text-sm font-bold text-slate-400 uppercase mb-4 flex items-center gap-2">
                      <ArrowDownRight className="w-4 h-4 text-red-500" /> Bottom 3 Performers
                  </h3>
                  <div className="space-y-3">
                      {bottomPerformers.map((p, idx) => (
                          <div key={p.id} className="flex items-center justify-between p-3 bg-slate-950/50 rounded-xl border border-slate-800 hover:border-red-500/30 transition-colors">
                              <div className="flex items-center gap-3">
                                  <div className="w-6 h-6 rounded-full bg-red-500/10 text-red-500 flex items-center justify-center text-xs font-bold">
                                      {idx + 1}
                                  </div>
                                  <div>
                                      <p className="text-sm font-bold text-white truncate w-28">{p.address.split(',')[0]}</p>
                                      <p className="text-[10px] text-slate-500">{p.city}</p>
                                  </div>
                              </div>
                              <div className="text-right">
                                  <p className="text-red-400 font-mono font-bold text-sm">€{p.monthlyRent - p.expenses}</p>
                                  <button className="text-[9px] text-slate-400 underline hover:text-white mt-0.5">Suggest Action</button>
                              </div>
                          </div>
                      ))}
                  </div>
              </div>

              {/* Smart Alerts */}
              <div className="bg-slate-900/60 backdrop-blur-md border border-slate-800 rounded-3xl p-6">
                  <div className="flex justify-between items-center mb-4">
                      <h3 className="text-sm font-bold text-slate-400 uppercase flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-500" /> Active Alerts
                      </h3>
                      {alerts.length > 0 && <span className="text-[10px] bg-red-500 text-white px-1.5 rounded-full">{alerts.length}</span>}
                  </div>
                  <div className="space-y-3">
                      {alerts.length > 0 ? alerts.map((alert, i) => (
                          <div key={i} className={`flex items-start gap-3 p-3 rounded-xl border ${alert.type === 'critical' ? 'bg-red-500/10 border-red-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
                              <alert.icon className={`w-4 h-4 mt-0.5 ${alert.type === 'critical' ? 'text-red-500' : 'text-amber-500'}`} />
                              <div>
                                  <p className="text-xs font-bold text-slate-200">{alert.msg}</p>
                                  <button className="text-[10px] text-slate-500 hover:text-white mt-1">View Details</button>
                              </div>
                          </div>
                      )) : (
                          <div className="text-center py-8 text-slate-600">
                              <CheckCircle2 className="w-8 h-8 mx-auto mb-2 opacity-20" />
                              <p className="text-xs">System Healthy</p>
                          </div>
                      )}
                  </div>
              </div>
          </div>
      </div>

      {/* 3. DETAILED PROPERTY COMPARISON GRID */}
      <div className="bg-slate-900/60 backdrop-blur-md border border-slate-800 rounded-3xl overflow-hidden">
          <div className="p-6 border-b border-slate-800">
              <h3 className="text-xl font-bold text-white">Asset Performance Matrix</h3>
          </div>
          <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-slate-400">
                  <thead className="bg-slate-950/50 text-xs font-bold uppercase tracking-wider text-slate-500">
                      <tr>
                          <th className="px-6 py-4">Property</th>
                          <th className="px-6 py-4 text-right">Revenue (Mo)</th>
                          <th className="px-6 py-4 text-right">Net Profit</th>
                          <th className="px-6 py-4 text-right">ROI</th>
                          <th className="px-6 py-4 text-center">Occupancy</th>
                          <th className="px-6 py-4 text-right">Cap Rate</th>
                          <th className="px-6 py-4 w-48">Trend (12mo)</th>
                          <th className="px-6 py-4"></th>
                      </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                      {rankedProperties.map(p => {
                          const roi = p.purchasePrice > 0 ? (((p.monthlyRent - p.expenses) * 12) / p.purchasePrice) * 100 : 0;
                          const capRate = roi; // Simplified cap rate = roi on purchase price for row display
                          
                          return (
                              <tr key={p.id} className="hover:bg-slate-800/30 transition-colors group">
                                  <td className="px-6 py-4">
                                      <div className="font-bold text-white">{p.address}</div>
                                      <div className="text-xs">{p.city}</div>
                                  </td>
                                  <td className="px-6 py-4 text-right font-mono text-slate-300">€{p.monthlyRent.toLocaleString()}</td>
                                  <td className="px-6 py-4 text-right font-mono font-bold text-emerald-400">€{(p.monthlyRent - p.expenses).toLocaleString()}</td>
                                  <td className="px-6 py-4 text-right font-mono text-blue-400">{roi.toFixed(1)}%</td>
                                  <td className="px-6 py-4 text-center">
                                      <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${p.status === 'Occupied' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                                          {p.status}
                                      </span>
                                  </td>
                                  <td className="px-6 py-4 text-right font-mono">{capRate.toFixed(1)}%</td>
                                  <td className="px-6 py-2">
                                      <div className="h-10 w-32">
                                          <ResponsiveContainer width="100%" height="100%">
                                              <LineChart data={generateMockTrend(roi, 0.5)}>
                                                  <Line type="monotone" dataKey="value" stroke={roi > 5 ? '#10b981' : '#f59e0b'} strokeWidth={2} dot={false} />
                                              </LineChart>
                                          </ResponsiveContainer>
                                      </div>
                                  </td>
                                  <td className="px-6 py-4 text-right">
                                      <button className="p-2 hover:bg-slate-800 rounded-lg text-slate-500 hover:text-white opacity-0 group-hover:opacity-100 transition-all">
                                          <ArrowUpRight className="w-4 h-4" />
                                      </button>
                                  </td>
                              </tr>
                          );
                      })}
                  </tbody>
              </table>
          </div>
      </div>

    </div>
    )
  );

};

// Helper Sub-Component for KPI Cards
const KPICard = ({ label, value, sub, icon: Icon, color }: any) => {
    const colorClasses: Record<string, string> = {
        blue: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
        emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
        indigo: 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20',
        amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
        red: 'text-red-400 bg-red-500/10 border-red-500/20',
    };

    return (
        <MotionDiv 
            whileHover={{ y: -2 }}
            className={`p-5 rounded-2xl border ${colorClasses[color] || 'border-slate-800 bg-slate-900'} relative overflow-hidden`}
        >
            <div className="flex justify-between items-start mb-2">
                <p className="text-[10px] uppercase font-bold opacity-70 tracking-wider">{label}</p>
                <Icon className="w-4 h-4 opacity-80" />
            </div>
            <h3 className="text-2xl font-black tracking-tight">{value}</h3>
            <p className="text-[10px] mt-1 opacity-60 font-medium">{sub}</p>
        </MotionDiv>
    );
}

export default Analytics;
